import type { RoleId } from '@onuw/roles-data'
import type { RoleGuide } from './types'

export const VAMPIRE_GUIDES = {
  copycat: {
    lead: 'Looks at a center card and becomes that role for the rest of the game.',
    does: [
      'The Copycat wakes before almost everyone and must look at one center card. He becomes that role, with its team and its night action, which he plays at that role’s usual time.',
    ],
    rules: [
      'The copy belongs to the Copycat card: if the card is moved later, whoever holds it is the copied role.',
      'The card he looked at stays in the center.',
      'A [[doppelganger]] who looks at the Copycat becomes what he became, without being told what that is.',
    ],
    tips: [
      'You know one center card, and you are it. Unless the deck holds two of that role, nobody else was dealt it, so claim it with confidence.',
      'Copying a Werewolf puts you in the pack: you wake with the wolves and win with them.',
    ],
    against: [
      'A Copycat explains a second claim of a one-of-a-kind role. Ask which center card they looked at, and check it against anyone else who saw the center.',
    ],
  },
  vampire: {
    lead: 'The Vampires wake first, at dusk, and make one more player a Vampire.',
    does: [
      'The Vampires (the Vampire, the [[count|Count]] and the [[master|Master]]) wake together, see each other and agree on one player who is not a Vampire. That player gets the Mark of the Vampire. A lone Vampire chooses alone.',
    ],
    rules: [
      'The marked player is a Vampire from then on: they win with the Vampires, and the village may win by killing them.',
      'They find out when everyone looks at their own mark at the end of dusk, unless a later mark replaced it first.',
      'The Vampires win if no Vampire dies, by card or by mark. If the [[tanner]] dies, they lose.',
      'With Vampires and Werewolves both among the players, the village needs a death on each team, which usually takes a tied vote.',
    ],
    tips: [
      'Mark the player the village trusts most. A villager who sees a Mark of the Vampire has until morning to learn to lie.',
      'The marked player knows they are a Vampire, not who you are. Find each other in the conversation.',
    ],
    against: [
      'When dusk ends everyone knows their own mark. A villager who says "mine is Clarity" may be the new Vampire. The [[marksman]] can check a mark, the [[priest]] can wipe one and the [[pickpocket]] can steal one.',
    ],
  },
  count: {
    lead: 'A Vampire who also hands out the Mark of Fear: the player who has it does nothing at night.',
    does: [
      'The Count wakes with the Vampires. Then he wakes again alone and must give the Mark of Fear to a player who is neither a Vampire nor the one just marked.',
    ],
    rules: [
      'A player who sees a Mark of Fear when dusk ends takes no night action. A feared [[seer]] does not look, a feared [[robber]] does not rob, and a feared [[werewolf]] keeps their eyes closed, unseen by the pack.',
      'What happened at dusk stands: the fear stops night actions only.',
      'The night still pauses for the feared role, so the timing tells nobody.',
      'The [[priest]] acts later at dusk and can replace the Mark of Fear before anyone looks at it.',
    ],
    tips: [
      'Fear the role that would hurt you most: the Seer, the [[marksman]], the [[revealer]].',
      'In the morning the feared player can only say "I did nothing", which is honest and impossible to check. Use it against them.',
    ],
    against: [
      'A player who says they were feared is telling you a Count is at the table. Believe it or not, it narrows the deck.',
    ],
  },
  master: {
    lead: 'A Vampire who cannot die when another Vampire votes for him.',
    does: [
      'The Master wakes with the Vampires and helps choose who gets the Mark of the Vampire. He has no other night action.',
    ],
    rules: [
      'If any other Vampire, by card or by the Mark of the Vampire, votes for the Master, he cannot be killed: not by the vote, and not by the [[hunter]].',
      'The player with the next most votes then dies instead, provided they have at least two.',
      'A Mark of Love still kills him.',
    ],
    tips: [
      'Have one Vampire vote for you. From the newly marked player, a vote for you also looks like an accusation.',
    ],
    against: [
      'The village needs a Vampire other than the Master to die, or the Master with no Vampire’s vote on him. Spread the suspicion.',
    ],
  },
  renfield: {
    lead: 'The Vampires’ servant: he sees them, and they do not see him.',
    does: [
      'Renfield wakes after the Vampires, sees who they are and which player they marked, and gives himself the Mark of the Bat.',
    ],
    rules: [
      'He wins with the Vampires and is not one: killing him does not help the village.',
      'The Mark of the Bat does nothing. It replaces whatever mark he had, a Mark of the Vampire included.',
      'With no Vampire among the players, by card or by mark, Renfield plays with the village.',
    ],
    tips: ['You know the Vampires and their new recruit. Take the suspicion that is heading their way.'],
    against: [
      'A player who gets themselves voted out for no clear reason may be Renfield. His death costs the Vampires nothing.',
    ],
  },
  diseased: {
    lead: 'Gives a neighbour the Mark of Disease: whoever votes for that player cannot win.',
    does: [
      'At dusk the Diseased must give the Mark of Disease to the player on their left or the player on their right.',
    ],
    rules: [
      'Everyone who votes for the player with the Mark of Disease loses, even when their team wins.',
      'The marked player can still die, and can still win.',
      'The mark can be replaced or moved after it is given: by the [[priest]], the [[pickpocket]] or the [[gremlin]].',
    ],
    tips: [
      'Mark the neighbour you believe is a villager and say so in the morning: nobody can afford to vote for them.',
      'On a wolf the mark is a gift: every vote against them is a lost game for the voter.',
    ],
    against: [
      'The marked player learns it when dusk ends. A wolf with the Mark of Disease will tell everyone.',
    ],
  },
  cupid: {
    lead: 'Gives two players a Mark of Love: if one of them dies, the other dies too.',
    does: [
      'At dusk Cupid may give Marks of Love to any two players, Cupid included. When dusk ends, the lovers wake and see each other.',
    ],
    rules: [
      'When one lover dies, so does the other, even a player who could not otherwise be killed.',
      'Love links two deaths, not two teams: each lover still wins or loses with their own side.',
      'A single Mark of Love does nothing. If one was replaced or stolen, the remaining lover wakes alone and knows it.',
    ],
    tips: [
      'Link a player you suspect to one the table can be talked into voting out. If either dies, both do.',
      'The lovers know each other, and you know both. That is three players who can check each other’s stories.',
    ],
    against: [
      'A wolf in love can be killed through their lover. A villager who finds they love a wolf can simply ask to be voted out.',
    ],
  },
  instigator: {
    lead: 'Gives any player the Mark of the Traitor: they win only if someone else on their own team dies.',
    does: ['At dusk the Instigator may give the Mark of the Traitor to any player, himself included.'],
    rules: [
      'The traitor wins if another member of their team dies, and loses otherwise, whatever their team does.',
      'A player alone on their team is not affected: the only Werewolf among the players, the [[blob]], the [[mortician]], the [[assassin]].',
    ],
    tips: [
      'On a wolf the mark makes them want their partner dead, which is what the village wants. Give it to the player you think is a wolf.',
      'On a villager it makes an enemy of the village who knows nothing. Think before marking the quiet player.',
    ],
    against: [
      'A villager with the Mark of the Traitor wins by getting any other villager killed. Their accusations sound honest and aim wrong.',
    ],
  },
  priest: {
    lead: 'Takes a Mark of Clarity and may give another to one player, wiping whatever mark they had.',
    does: [
      'At dusk the Priest puts a Mark of Clarity in front of himself, and may put one in front of another player.',
    ],
    rules: [
      'His own Mark of Clarity is not optional.',
      'Marks go down in wake order. The Priest comes after the Vampires, the [[count|Count]], the [[diseased]], [[cupid]] and the [[instigator]], so his mark replaces theirs. Only the [[assassin]] and the [[appAssassin]] mark after him.',
      'A Mark of Clarity does nothing: the card alone decides role and team.',
    ],
    tips: [
      'You are safe from every mark given before yours. Say so in the morning.',
      'Cleanse the player you most need to still be what they were dealt.',
    ],
    against: [
      'The Vampires cannot be sure their mark survived dusk. The player they chose may have looked and seen Clarity.',
    ],
  },
  assassin: {
    lead: 'Marks one player and wins if that player dies.',
    does: [
      'At dusk the Assassin must give the Mark of the Assassin to any player, himself included. If an [[appAssassin]] is at the table, the two see each other.',
    ],
    rules: [
      'The Assassin wins if the player with the mark dies, whichever team wins as well. He belongs to no team.',
      'The mark can move. He wins if whoever has it when the night ends dies.',
      'With no Mark of the Assassin in play (the card reached a player only at night), its holder is a plain villager.',
    ],
    tips: [
      'Mark a player who will be easy to vote out, then argue against them all day. Your accusation need not be true.',
      'Marking yourself makes you a second Tanner. The table will not expect it.',
    ],
    against: [
      'A player who pushes hard without a story of their own may be the Assassin. Ask what they did at night: he acts only at dusk.',
    ],
  },
  appAssassin: {
    lead: 'Knows the Assassin and wins if he dies. With no Assassin at the table, she marks a target herself.',
    does: [
      'At dusk the Apprentice Assassin wakes right after the [[assassin]], while his eyes are still open: they see each other. If nobody is the Assassin, she must give the Mark of the Assassin to any player, herself included.',
    ],
    rules: [
      'With an Assassin among the players, she wins if he dies. Without one, she wins if the player with the Mark of the Assassin dies.',
      'The Assassin knows who she is and what she wants.',
      'With no Mark of the Assassin in play, she is a plain villager.',
    ],
    tips: [
      'The Assassin knows you want him dead. Make the table see him as you do before he discredits you.',
    ],
    against: [
      'As the Assassin, your apprentice needs you voted out. Give the table a reason to doubt them first.',
    ],
  },
  marksman: {
    lead: 'Looks at one player’s card and at another player’s mark.',
    does: ['The Marksman may look at one other player’s card, and then at the mark of a different player.'],
    rules: [
      'The card and the mark must belong to two different players, and either look may be skipped.',
      'A shield hides the card under it, not the mark beside it.',
      'He wakes after the [[seer]] and before the [[robber]]. The [[pickpocket]] and the [[gremlin]] can still move the mark he saw.',
    ],
    tips: [
      'Spend the mark look on the player you fear the Vampires chose, and the card look on somebody else.',
    ],
    against: [
      'With a Marksman in the deck, a claim about your own mark can be checked. Lie about it only if you must.',
    ],
  },
  pickpocket: {
    lead: 'Trades marks with another player, then looks at the mark he took.',
    does: ['The Pickpocket may swap his own mark with another player’s and look at his new one.'],
    rules: [
      'The other player is not told. They still believe they have the mark they saw when dusk ended.',
      'A stolen Mark of the Vampire makes the Pickpocket a Vampire, and the player he took it from is one no longer.',
      'A stolen Mark of Love makes him the other lover’s partner.',
      'A shield protects a card, not a mark.',
    ],
    tips: [
      'Like the [[robber]], you may change sides by what you take. You also cure the player you rob: your old mark is theirs now.',
    ],
    against: ['After a Pickpocket, your mark may not be the one you looked at. Only the Pickpocket knows.'],
  },
  gremlin: {
    lead: 'Swaps two players’ cards or, with marks in play, their marks. One of them may be his own.',
    does: [
      'The Gremlin may exchange the cards of any two players, himself included, without looking at them. With marks in play he chooses whether to swap two cards or two marks.',
    ],
    rules: [
      'Cards or marks, not both.',
      'He wakes after the [[troublemaker]], the [[villageIdiot]] and the [[auraSeer]], and before the [[drunk]].',
      'A shield keeps a card where it is, but not the mark beside it.',
      'When he swaps his own card he does not see what he got.',
    ],
    tips: [
      'Swapping your own card with a suspected wolf makes you the wolf. Do it only if you mean to change sides.',
      'Swapping two marks can take a Mark of the Vampire off a player without anyone knowing, the player included.',
    ],
    against: [
      'The Gremlin acts after the Troublemaker, so a card she swapped may have been swapped again. The [[insomniac]] still sees the final result.',
    ],
  },
} satisfies Partial<Record<RoleId, RoleGuide>>
